<template>
  <div class="">
    <div class="p-4">
      <span class="text-lg font-semibold">Reimbursements</span>
    </div>

    <v-data-table
      :headers="headers"
      :items="reimbursements"
      :loading="loading"
      :items-per-page="10"
      class="table-radius"
      item-key="_id"
    >
      <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
        <p
          :key="`tooltip-${index}`"
          class="font-title"
          v-if="header.text?.length > 15"
          v-tooltip="{ text: header.text }"
        >
          {{ header.text }}
        </p>
        <p :key="`normal-${index}`" v-else class="font-title">{{ header.text }}</p>
      </template>
      <template #[`item.billNumber`]="{ item }">
        <td class="px-4 py-4 table-font">{{ item.billNumber }}</td>
      </template>
      <template #[`item.claimAmount`]="{ item }">
        <td class="px-4 py-4 table-font">₹{{ item.claimAmount }}</td>
      </template>

      <template #[`item.billDate`]="{ item }">
        <td class="px-4 py-4 table-font">
          {{ $dayjs(item.billDate).format('DD/MM/YYYY') }}
        </td>
      </template>

      <template #[`item.month`]="{ item }">
        <td class="px-4 py-4 table-font">{{ item.month }} {{ item.year }}</td>
      </template>

      <template #[`item.reimbursementType`]="{ item }">
        <td class="px-4 py-4 table-font">
          <v-chip small label color="blue" text-color="white">
            {{ getReimbursementTypeName(item.reimbursementType) }}
          </v-chip>
        </td>
      </template>

      <template #[`item.comments`]="{ item }">
        <td class="px-4 font-semibold">
          <div class="flex items-center gap-2">
            <p class="ellipsisClass w-32 mb-0">
              {{
                Array.isArray(item.comments) && item.comments.length > 0
                  ? item.comments[item.comments.length - 1].message
                  : item.comments
              }}
            </p>
            <v-tooltip top v-if="Array.isArray(item.comments) && item.comments.length">
              <template v-slot:activator="{ on: tooltipOn }">
                <v-icon
                  v-on="{ ...tooltipOn, ...on }"
                  style="font-size: 20px"
                  color="#fb8c00"
                  v-tooltip="{
                    text: getCommentsHtml(item.comments),
                    trigger: 'click',
                    width: '300px',
                    height: '200px'
                  }"
                >
                  mdi-information
                </v-icon>
              </template>
              <span>See Comments</span>
            </v-tooltip>
          </div>
        </td>
      </template>

      <template #[`item.attachedBill`]="{ item }">
        <td class="px-4 py-4 table-font">
          <v-btn
            v-if="item.attachedBill"
            icon
            @click="viewBill(item.attachedBill)"
            title="View Bill"
          >
            <v-icon>mdi-file-document</v-icon>
          </v-btn>
        </td>
      </template>
      <template #[`item.status`]="{ item }">
        <td class="px-4 py-4 table-font">
          <OrderStatuses
            :orderStatus="
              Array.isArray(item.approvals) && item.approvals.length > 0
                ? item.approvals[item.approvals.length - 1].status
                : 'Pending'
            "
            :statuses="getRequestStatuses(item)"
            @statusChange="(status) => updateStatus(status, item._id)"
          />
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="showBillDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-md font-semibold">Bill Attachment</span>
        </v-card-title>
        <v-card-text>
          <v-img v-if="billImageUrl" :src="billImageUrl" contain max-height="600px"></v-img>
          <v-alert v-else type="warning"> Bill image not available </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showBillDialog = false"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import OrderStatuses from '@/components/Workforce/InventoryManagement/OrderStatuses.vue'
export default {
  name: 'ReimbursementTable',
  props: {
    reimbursements: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  components: {
    OrderStatuses
  },
  data() {
    return {
      search: '',
      showBillDialog: false,
      billImageUrl: '',
      selectedReimbursement: null,
      statuses: ['Pending', 'Approved', 'Rejected', 'Review', 'Escalated', 'Resubmitted'],
      headers: [
        {
          text: 'Bill Number',
          align: 'start',
          value: 'billNumber',
          sortable: false
        },
        {
          text: 'Reimbursement Type',
          value: 'reimbursementType',
          sortable: false
        },
        {
          text: 'Month',
          value: 'month',
          sortable: false
        },
        {
          text: 'Bill Date',
          value: 'billDate',
          sortable: false
        },
        {
          text: 'Claim Amount',
          value: 'claimAmount',
          sortable: false
        },
        {
          text: 'Comments',
          value: 'comments',
          sortable: false
        },
        {
          text: 'Attached Bill',
          value: 'attachedBill',
          sortable: false
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false
        }
      ]
    }
  },

  methods: {
    viewBill(billPath) {
      // Construct the full URL for the bill image
      const baseUrl = process.env.VUE_APP_S3_BASE_URL || ''
      this.billImageUrl = billPath.startsWith('http') ? billPath : `${baseUrl}${billPath}`
      this.showBillDialog = true
    },
    getReimbursementTypeName(typeId) {
      // Find the reimbursement type in the reimbursementTypes array
      const type = this.reimbursementTypes?.find((t) => t._id === typeId)
      return type ? type.name : 'Unknown Type'
    },
    getRequestStatuses(item) {
      // Return statuses formatted for OrderStatuses component
      return this.statuses.map((status) => ({
        label: status,
        value: status
      }))
    },
    async updateStatus(status, itemId) {
      try {
        const item = this.reimbursements.find((r) => r._id === itemId)
        const currentApprovals = item && item.approvals ? [...item.approvals] : []

        currentApprovals.push({
          status: status,
          updatedBy: JSON.parse(localStorage.getItem('user'))?._id,
          updatedAt: new Date()
        })

        await this.$axios.put(`/orgSetup/reimbursement/${itemId}`, {
          approvals: currentApprovals
        })
        this.$emit('statusUpdated')
        this.$toast.success('Status updated successfully')
      } catch (error) {
        console.error('Error updating status:', error)
        this.$toast.error('Failed to update status')
      }
    },
    getCommentsHtml(comments) {
      if (!comments || !comments.length) return ''
      return comments
        .map(
          (c) =>
            `<div style='margin-bottom: 8px; text-align: left; border-bottom: 1px solid #555; padding-bottom: 4px;'>
              <div style='font-weight: bold; font-size: 12px;'>${this.$dayjs(c.commentedAt).format(
                'DD MMM YYYY, hh:mm A'
              )}</div>
              <div style='font-size: 13px;'>${c.message}</div>
            </div>`
        )
        .join('')
    }
  }
}
</script>

<style scoped>
.v-data-table {
  margin-top: 16px;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
